import { gql } from './client';
import { emit, withExpected } from './events';

// Every editable setting except the id/scope/timestamps: free-text prompts and Bedrock model ids.
export const SETTING_TEXT_KEYS = [
  'policyAnalysisPrompt', 'applicationAnalysisPrompt', 'preApplicationPrompt', 'assessmentPreamble', 'siteNamePrompt',
  'chunkMapPrompt', 'chunkReducePrompt', 'clueExtractionPrompt', 'placementCheckPrompt',
] as const;
export const SETTING_MODEL_KEYS = ['modelId', 'clueExtractionModelId', 'placementCheckModelId', 'chunkMapModelId'] as const;
// On/off switches, stored as 'true' or empty.
export const SETTING_FLAG_KEYS = ['mastermapEnabled'] as const;
export type SettingKey = typeof SETTING_TEXT_KEYS[number] | typeof SETTING_MODEL_KEYS[number] | typeof SETTING_FLAG_KEYS[number];

export type PlanningSettings = {
  id: string;
  scope: 'Global';
  createdAt?: string;
  updatedAt?: string;
  /** Last four characters of the OS Data Hub key; the key itself is never readable. */
  osApiKeyHint?: string;
} & { [K in SettingKey]?: string };

export interface AvailableModel {
  id: string;
  name: string;
}

export async function listPlanningSettings(): Promise<PlanningSettings[]> {
  const data = await gql<{ elevationsPlanningSettingsList: PlanningSettings[] }>(
    `query ListPlanningSettings {
      elevationsPlanningSettingsList { id scope ${[...SETTING_TEXT_KEYS, ...SETTING_MODEL_KEYS, ...SETTING_FLAG_KEYS].join(' ')} osApiKeyHint createdAt updatedAt }
    }`,
  );
  return data.elevationsPlanningSettingsList ?? [];
}

// Live Bedrock model availability (ids are account-specific); a read-only listing call.
export async function listAvailableBedrockModels(): Promise<AvailableModel[]> {
  const data = await gql<{ elevationsPlanningAvailableBedrockModels: AvailableModel[] }>(
    `query ListAvailableBedrockModels { elevationsPlanningAvailableBedrockModels { id name } }`,
  );
  return data.elevationsPlanningAvailableBedrockModels ?? [];
}

export function emitUpsertPlanningSettings(
  payload: { scope: 'Global'; performedBy?: string } & { [K in SettingKey]?: string },
  connectionId?: string,
  expectedValues?: Record<string, unknown>,
): Promise<string> {
  return emit('UpsertPlanningSettings', crypto.randomUUID(), withExpected(payload, expectedValues), connectionId);
}

// Sets the OS Data Hub key used to match location plans against OS MasterMap. The workflow checks the user may manage
// settings, then the locate service stores the key where only it can read it; Settings only ever sees its last four characters.
export function emitSetOsApiKey(key: string, performedBy: string | undefined, connectionId?: string): Promise<string> {
  return emit('SetOsApiKey', crypto.randomUUID(), { key, performedBy }, connectionId);
}
